import { useState } from "react";
import { cn } from "../lib/utils";

export interface AppNotification {
  id: string;
  title: string;
  description?: string;
  time?: string;
  read?: boolean;
  type?: "info" | "success" | "warning" | "error";
  avatar?: string;
  avatarFallback?: string;
}

interface NotificationBellProps {
  notifications?: AppNotification[];
  onMarkRead?: (id: string) => void;
  onMarkAllRead?: () => void;
  onDismiss?: (id: string) => void;
  className?: string;
}

const typeColor: Record<string, string> = {
  info: "#6366f1",
  success: "#22c55e",
  warning: "#f59e0b",
  error: "#ef4444",
};

export function NotificationBell({
  notifications = [],
  onMarkRead,
  onMarkAllRead,
  onDismiss,
  className,
}: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const unread = notifications.filter(n => !n.read).length;

  function handleMarkRead(id: string) {
    onMarkRead?.(id);
  }

  return (
    <div className={cn("nb-root", className)}>
      <button
        className={cn("nb-trigger", open && "nb-trigger-open")}
        aria-label={`${unread} unread notifications`}
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span className="nb-badge">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>

      {open && (
        <div className="nb-panel" role="dialog" aria-label="Notifications">
          <div className="nb-header">
            <span className="nb-header-title">Notifications</span>
            {unread > 0 && (
              <button
                className="nb-mark-all"
                onClick={() => { onMarkAllRead?.(); }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="nb-list">
            {notifications.length === 0 ? (
              <div className="nb-empty">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <p>All caught up!</p>
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  className={cn("nb-item", !n.read && "nb-item-unread")}
                  onClick={() => handleMarkRead(n.id)}
                >
                  <div className="nb-item-indicator">
                    {n.avatarFallback ? (
                      <div
                        className="nb-avatar"
                        style={{ background: typeColor[n.type ?? "info"] }}
                      >
                        {n.avatarFallback}
                      </div>
                    ) : (
                      <span
                        className="nb-dot"
                        style={{ background: typeColor[n.type ?? "info"] }}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className="nb-item-body">
                    <div className="nb-item-top">
                      <p className="nb-item-title">{n.title}</p>
                      {n.time && <span className="nb-item-time">{n.time}</span>}
                    </div>
                    {n.description && (
                      <p className="nb-item-desc">{n.description}</p>
                    )}
                  </div>
                  {onDismiss && (
                    <button
                      className="nb-dismiss"
                      aria-label="Dismiss"
                      onClick={e => { e.stopPropagation(); onDismiss(n.id); }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
